import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import Button from 'components/UI/Button';
import Modal from 'components/Modal';
import { createMultipleReference, createReference } from 'modules/bancomer/actions';
import { ReactComponent as CardIcon } from 'assets/icons/card.svg';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { CONTRACT, SECTION } from 'constants/global';
import { ROUTE_PATH } from 'routes';
import { contractsActions } from 'modules/contracts';
import { sectionsActions } from 'modules/sections';
import platform from 'utils/platform';
import BBVAForm from './components/BBVAForm';
import './styles.scss';

// Multipagos (Adquira) validates these as external redirect targets, so they
// must be absolute URLs. API_BASE_URL is relative ("/api") under the dev proxy,
// which Multipagos rejects with "mp_urlsuccess con formato inválido"; always use
// the absolute API origin from config.
const PAYMENT_CALLBACK_BASE_URL = window.MAIN_API_URL;

const FORM = {
  mp_account: window.BANCOMER.MP_ACCOUNT,
  mp_product: '1',
  mp_order: 'B00001',
  mp_reference: '',
  mp_node: '2',
  mp_concept: '3',
  mp_currency: '1',
  mp_amount: '0',
  mp_urlsuccess: `${PAYMENT_CALLBACK_BASE_URL}/bbva/payments/success`,
  mp_urlfailure: `${PAYMENT_CALLBACK_BASE_URL}/bbva/payments/failure`,
  mp_signature: '',
};

const BBVA = ({
  id = '',
  type = '',
  payment = null,
  orders = [],
  amount,
  coverage = 0,
  coverageId = null,
  isMultiple = false,
  onClose,
}) => {
  const literals = useLiterals('contractPay');
  const [formValue, setFormValue] = useState(FORM);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Native apps request the external-window flow via ?bancomerExternalRedirect=true
  // (the bank form can't post inside their webview); web posts the form inline.
  const externalRedirect = window?.mlsQueryParams?.bancomerExternalRedirect;

  useEffect(() => {
    if (shouldSubmit) {
      if (externalRedirect) {
        if (type === CONTRACT) {
          dispatch(contractsActions.needUpdate());
        } else {
          dispatch(sectionsActions.needUpdate());
        }
        const paymentParams = btoa(JSON.stringify(formValue));
        window.openDobleAuth(`${document.location.origin}${ROUTE_PATH.setBBVARedirect()}?payment=${paymentParams}`);
        setTimeout(() => {
          navigate(ROUTE_PATH.PAYMENTS);
        }, 500);
      } else {
        formRef.current.submit();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit]);

  const handleConfirmModal = () => {
    setLoading(true);
    const callback = (response) => {
      setLoading(false);
      if (response) {
        setFormValue({
          ...formValue,
          mp_node: platform.isNativeApp ? '2' : '3',
          mp_concept: platform.isNativeApp ? '3' : '4',
          mp_amount: response.amount,
          mp_reference: response.id,
          mp_order: response.external_order,
          mp_signature: response.signature,
        });
        setShouldSubmit(true);
      } else {
        setError(true);
      }
    };

    if (isMultiple) {
      createMultipleReference(amount, orders).then(callback);
    } else {
      createReference({
        contractType: type,
        contract_id: type === CONTRACT ? id : null,
        section_id: type === SECTION ? id : null,
        product: formValue.mp_product,
        amount: amount || payment?.amount,
        monto: payment?.amount,
        coverage: coverage || 0,
        coverage_id: coverageId || null,
        currency: formValue.mp_currency,
        paymentKey: payment?.paymentKey || null,
        payment_name: payment?.title || null,
      }).then(callback);
    }
  };

  return (
    <>
      <Modal
        title={literals.bancomer.confirmTitle}
        onClose={onClose}
        onConfirm={handleConfirmModal}
      >
        <div className='text-center'>
          <CardIcon className='my-4' />
          <BBVAForm form={formValue} ref={formRef} />
          <div className='mb-6'>{literals.bancomer.confirmDescription}</div>
          {externalRedirect ? (
            <div className='text-neutral-500 mb-6'>{literals.bancomer.confirmDescriptionExternalRedirect}</div>
          ) : null}
          <div className='flex flex-col gap-4 lg:flex-row lg:gap-3'>
            <Button
              disabled={loading}
              onClick={onClose}
              type='secondary'
              text={literals.common.cancel}
              className='lg:flex-1'
            />
            <Button
              loading={loading}
              onClick={handleConfirmModal}
              type='primary'
              text={literals.common.continue}
              className='lg:flex-1'
            />
          </div>
        </div>
      </Modal>
      {error && (
        <ErrorModal
          title={literals.bancomer.failureReferenceTitle}
          description={literals.bancomer.failureReferenceDescription}
          onClose={() => setError(false)}
        />
      )}
    </>
  );
};

BBVA.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  payment: PropTypes.object,
  orders: PropTypes.array,
  amount: PropTypes.number.isRequired,
  coverage: PropTypes.number,
  coverageId: PropTypes.string,
  isMultiple: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default BBVA;
